import { useCallback, useContext, useMemo, useState, MouseEvent } from "react"
import { GetApp } from "../../App"
import { classNames } from "../../Components"
import { BlockField, BlockRow } from "../../Components/Block"
import { UserAdviceDialog } from "../../Components/UserAdvice/UserAdvice"
import { CurrentLogApi, IAlertStatus, IAlertStatusType, IAlertLightsData, IAlertCommandAction, IAppState, ILogLighting } from "../../Types"
import { logger } from "../../App/Logger"
import { AlertAdvice } from "./AlertAdvice"
import { OverlayViewDefinition } from "../OverlayView"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceAlertViewProps } from "./Params"
import { AlertMessage } from "./AlertMessage"
import { GetShipUsers, ShipRoutes } from "../ShipView"
import { GetAppView, AppRoutes } from "../AppView"
import { Icon, Icons } from "../../Components/Icon"
import { isShipSkipper, mayClearAlert, shipLocalHour } from "./clearPermission"
import { useOverlayScroll } from "../../Components/useOverlayScroll"
import { OverlayScrollFooter } from "../../Components/OverlayScrollFooter"
import { ShipContext } from "../../App/Context"
import { lightingTranslationKey } from "../../Components/LogButton/Lighting"

/// The choices the dialog offers. `record` is not an alert command -- it captures
/// the recommended lighting instead -- but it sits in the same list because to the
/// user it is simply another way to answer "what should happen to this alert?".
type AlertChoice = IAlertCommandAction | "record";

export function UserAdviceAlertView({ advice: _advice, alert, state, initFailed, initConfirmingClear, canClear, day, isSkipper, hasSkipper, vesselType: suppliedVesselType }: UserAdviceAlertViewProps) {
  const app = GetApp();
  const shipContext = useContext(ShipContext);
  const vesselType = suppliedVesselType ?? shipContext?.ship?.vesselType;
  const t = app.getT("UserAdviceAlertView");
  const shipUsers = GetShipUsers();
  const navigateApp = GetAppView()?.navigateApp;
  const { bodyRef, showScrollButton, isAtBottom, handleScrollMore } = useOverlayScroll();

  const [failed, setFailed] = useState(initFailed ?? false);
  // Always starts unselected, and deliberately remembers nothing across openings:
  // every choice changes the alert's state, so a remembered selection could be
  // committed by a reflexive second click on the submit button.
  const [choice, setChoice] = useState<AlertChoice | undefined>(initConfirmingClear ? IAlertCommandAction.clear : undefined);
  const [busy, setBusy] = useState(false);
  // Clearing is the one irreversible choice -- it ends the alert whether or not the
  // situation actually holds -- so it is confirmed before it fires. The prompt
  // replaces this dialog's own content rather than stacking a second overlay on
  // top of it, which would bury the alert the crew is deciding about.
  const [confirmingClear, setConfirmingClear] = useState(initConfirmingClear ?? false);

  const lightsData = useMemo(() => {
    if (alert.name != IAlertStatusType.lights) {
      return undefined;
    }
    return JSON.parse(alert.data || "{}") as IAlertLightsData;
  }, [alert]);

  const closeAlert = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    state.setSelected(undefined);
  }, [state]);

  const openSettings = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // For navigation lights alerts, open the lights reminder settings dialog instead
    // of navigating to ship settings (#1171).
    if (alert.name == IAlertStatusType.lights) {
      state.setSelected("lights-toolbar");
      return;
    }
    state.setSelected(undefined);
    if (app.shipId != undefined && navigateApp) {
      navigateApp(AppRoutes.ship, { shipId: app.shipId, shipRoute: ShipRoutes.settings });
    }
  }, [alert.name, app.shipId, navigateApp, state]);

  // Record the recommended lighting straight from the advice card. The capture
  // creates a log + single-active `.lighting` status; the reminder auto-resolves
  // on the next evaluation once the recorded lighting matches. Dismiss the dialog
  // first (like the alert-command controls) so the capture never blocks it.
  const recordRecommended = useCallback(() => {
    const recommended = lightsData?.recommended;
    state.setSelected(undefined);
    if (recommended != undefined) {
      try {
        new CurrentLogApi(app.apiClient).current.lighting({ params: {}, body: { lighting: recommended } }).call().catch((err) => logger.debug("recordRecommended", err));
      } catch (err) {
        logger.debug("recordRecommended", err);
      }
    }
  }, [app, lightsData, state]);

  // Snooze / acknowledge / clear an open alert through the shared alert-command
  // route. The command carries the alert's episode id so it addresses exactly this
  // occurrence — alerts that can have several open at once (messages, MOB beacons)
  // would otherwise all be acted on together (#1269). Only dismiss the dialog once
  // the command actually succeeds — e.g. a user without sufficient permission gets
  // a 401, which must stay visible instead of being swallowed behind an
  // optimistically-closed dialog (#1146).
  const command = useCallback(async (action: IAlertCommandAction) => {
    if (!alert.name) {
      return;
    }
    setFailed(false);
    try {
      await UserAdviceViewsData.alertCommand(app.apiClient, alert.name, { action, episodeId: AlertAdvice.episodeKey(alert) });
      state.setSelected(undefined);
    } catch (e) {
      logger.error("alert command", e);
      setFailed(true);
    }
  }, [app, alert, state]);

  const perform = useCallback(async (chosen: AlertChoice) => {
    setBusy(true);
    try {
      if (chosen == "record") {
        recordRecommended();
      } else {
        await command(chosen);
      }
    } finally {
      setBusy(false);
    }
  }, [command, recordRecommended]);

  const submit = useCallback(async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (choice == undefined || busy) {
      return;
    }
    if (choice == IAlertCommandAction.clear) {
      setFailed(false);
      setConfirmingClear(true);
      return;
    }
    await perform(choice);
  }, [busy, choice, perform]);

  const confirmClear = useCallback(async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) {
      return;
    }
    await perform(IAlertCommandAction.clear);
  }, [busy, perform]);

  // Back to the choices with `clear` still selected, so a mis-click costs one click
  // rather than making the crew re-read the whole list.
  const cancelClear = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmingClear(false);
  }, []);

  // Only sounding/notifying alerts are actionable; a resolved/notice alert has
  // nothing to acknowledge.
  const actionable = alert.value == IAlertStatus.alarm || alert.value == IAlertStatus.warning;

  // Hidden from the skipper, for whom handing the alert to the skipper is a no-op.
  // Computed here for the same reason as the clear permission: the day's skipper is
  // a ShipUser.id and the session knows an account User.id, and the day can change
  // under the dialog.
  const skipper = useMemo(() => {
    if (isSkipper !== undefined) {
      return isSkipper;
    }
    if (!day || !shipUsers || shipUsers.length == 0) {
      return false;
    }
    return isShipSkipper({ accountUserId: app.user?.id, shipUsers, day });
  }, [app.user?.id, day, isSkipper, shipUsers]);
  const skipperConfigured = hasSkipper ?? day?.skipper != undefined;

  const choices: AlertChoice[] = useMemo(() => {
    const list: AlertChoice[] = [];
    if (lightsData && lightsData.recommended != undefined) {
      list.push("record");
    }
    if (actionable) {
      // Ordered by how far each one commits you: silence it for a few minutes,
      // mark it seen, hand it to the skipper, close it. `clear` is deliberately
      // last -- it is the only choice that ends the alert outright, and the only
      // one that is permission-gated, so it should not sit in the middle of the
      // list where it can be picked by momentum.
      list.push(IAlertCommandAction.snooze, IAlertCommandAction.acknowledge);
      if (skipperConfigured && !skipper) {
        list.push(IAlertCommandAction.escalate);
      }
      list.push(IAlertCommandAction.clear);
    }
    return list;
  }, [actionable, lightsData, skipper, skipperConfigured]);

  // Only `clear` is permission-gated -- snooze and acknowledge are open to every
  // ship user. The answer depends on the ship-local hour (it changes as the watch
  // rotates), so it is recomputed here rather than delivered with the alert; the
  // rule itself is shared with the server via `Tests/Fixtures/alert-clear-permission.json`.
  //
  // `undefined` means "not known yet" -- no day, or the crew list still loading --
  // and counts as allowed: the server is the enforcer, so guessing "denied" here
  // would block a legitimate user for nothing, while guessing "allowed" costs at
  // worst a rejected command and the error message below.
  const mayClear = useMemo(() => {
    if (canClear !== undefined) {
      return canClear;
    }
    if (!day || !shipUsers || shipUsers.length == 0 || app.user?.id == undefined) {
      return undefined;
    }
    return mayClearAlert({ accountUserId: app.user.id, shipUsers, day, shipLocalHour: shipLocalHour(day.timezone) });
  }, [app.user?.id, canClear, day, shipUsers]);

  const clearDenied = (c: AlertChoice) => c == IAlertCommandAction.clear && mayClear === false;
  const choiceLabel = (c: AlertChoice) => c == "record"
    ? t("record", { lighting: app.t(lightingTranslationKey(lightsData?.recommended ?? ILogLighting.none, vesselType)) })
    : t(c);

  // The confirmation replaces this dialog's content instead of stacking a second
  // overlay: the crew is deciding whether to end *this* alert, so the alert itself
  // stays on screen and the choices step aside until the question is answered.
  if (confirmingClear) {
    return (
      <UserAdviceDialog
        className={classNames(["UserAdviceAlertView", "UserAdviceAlertView-confirming"])}
        bodyRef={bodyRef}
        scrollFooter={<OverlayScrollFooter isAtBottom={isAtBottom} showScrollButton={showScrollButton} handleScrollMore={handleScrollMore} readMoreLabel={t("read_more")} />}
      >
        <BlockRow><AlertMessage alert={alert} lightingIcons={true} vesselType={vesselType} /></BlockRow>
        <BlockRow className="block-row-infotext">
          <span className="UserAdviceAlertView-confirm">{t("clear_confirm_message")}</span>
        </BlockRow>
        {failed && <BlockRow><BlockField><span className="UserAdviceAlertView-error text-error">{t("error")}</span></BlockField></BlockRow>}
        <BlockRow actions={true} className="bottom">
          <BlockField><button className="button" onClick={cancelClear}>{t("clear_confirm_cancel")}</button></BlockField>
          <BlockField><button className="button default" disabled={busy} onClick={confirmClear}>{t("clear_confirm")}</button></BlockField>
        </BlockRow>
      </UserAdviceDialog>
    );
  }

  return (
    <UserAdviceDialog
      className={classNames(["UserAdviceAlertView"])}
      bodyRef={bodyRef}
      scrollFooter={<OverlayScrollFooter isAtBottom={isAtBottom} showScrollButton={showScrollButton} handleScrollMore={handleScrollMore} readMoreLabel={t("read_more")} />}
    >
      <BlockRow><AlertMessage alert={alert} lightingIcons={true} vesselType={vesselType} /></BlockRow>
      {choices.length > 0 && (
        <BlockRow>
          <BlockField className="block-row-checkboxes UserAdviceAlertView-choices">
            {choices.map((c) => (
              // Each choice is its own block: the radio and its label on one line,
              // the explanation beneath. The explanation cannot live inside the
              // `BlockField` -- `labelAfter` renders the label *after* the children,
              // so it would end up between the radio and its own label.
              <div className="UserAdviceAlertView-choice" key={c}>
                <BlockField label={choiceLabel(c)} labelAfter={true} labelFor={`alert-choice-${c}`}>
                  <input
                    id={`alert-choice-${c}`}
                    name="alertChoice"
                    type="radio"
                    value={c}
                    checked={choice == c}
                    disabled={clearDenied(c)}
                    onChange={() => setChoice(c)}
                  />
                </BlockField>
                {/* Every choice explains itself up front rather than only once
                    selected: the texts are a line each, and the point of the
                    redesign is that the consequences can be weighed against one
                    another before anything is committed. */}
                <span className="UserAdviceAlertView-explanation">{t(`explain_${c}`)}</span>
              </div>
            ))}
          </BlockField>
        </BlockRow>
      )}
      {mayClear === false && choices.includes(IAlertCommandAction.clear) && (
        <BlockRow className="block-row-infotext">
          <span className="UserAdviceAlertView-denied">{t("clear_denied")}</span>
        </BlockRow>
      )}
      {failed && <BlockRow><BlockField><span className="UserAdviceAlertView-error text-error">{t("error")}</span></BlockField></BlockRow>}
      <BlockRow actions={true} className="bottom">
        {lightsData && (
          <BlockField>
            <button className="button" onClick={openSettings}><Icon icon={Icons.Settings} /> {t("settings")}</button>
          </BlockField>
        )}
        <BlockField><button className="button" onClick={closeAlert}>{t("cancel")}</button></BlockField>
        {choices.length > 0 && (
          <BlockField>
            <button className="button default" type="submit" disabled={choice == undefined || busy} onClick={submit}>
              {choice == undefined ? t("submit_none") : t("submit", { state: t(`state_${choice}`) })}
            </button>
          </BlockField>
        )}
      </BlockRow>
    </UserAdviceDialog>
  );
}

// This dialog's own overlay attributes (#1171). Declared here rather than in
// `UserAdviceOverlayView` so everything the alert dialog decides about itself -- its
// title, and the severity styling that paints the overlay red or orange -- stays in
// this module. Deliberately hook-free: `UserAdviceOverlayView` calls one of these per
// open advice, so a hook here would give React a different hook count per render.
export function userAdviceAlertOverlay(app: IAppState, props: UserAdviceAlertViewProps): OverlayViewDefinition {
  const t = app.getT("UserAdviceAlertView");
  const level = IAlertStatus[props.alert.value] || "default";
  return {
    close: true,
    className: "overlay-max-height",
    style: level as OverlayViewDefinition["style"],
    title: t("title", { name: t(`alert_${props.alert.name}`), level: t(`alert_${level}`) }),
    render: () => <UserAdviceAlertView {...props} />,
  };
}
